import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { TableCell, TableRow } from '../../../../components/ui/table';
import { getYouTubeThumbnail } from '../../../../lib/videoUtils';
import { ProgressIndicator } from './ProgressIndicator';
import getTranslation from '../../../../i18n/utils';

// Create motion-enabled TableRow component
const MotionTableRow = motion(TableRow);

interface FileListItemShellProps {
  /**
   * File name/title
   */
  name: string;
  /**
   * Thumbnail URL or preview URL
   */
  thumbnailUrl?: string;
  /**
   * YouTube URL for imports
   */
  youtubeUrl?: string;
  /**
   * Status text to display
   */
  statusText: string;
  /**
   * Upload/import progress (0-100) or undefined for indeterminate spinner
   */
  progress?: number;
  /**
   * Whether to show percentage in status text
   */
  showPercentage?: boolean;
  /**
   * File type for preview rendering
   */
  fileType?: string;
  /**
   * Whether this is a failed import/upload
   */
  isError?: boolean;
  /**
   * Callback to dismiss a failed import
   */
  onDismiss?: () => void;
  /**
   * Callback to cancel an in-progress upload
   */
  onCancel?: () => void;
}

/**
 * Unified list view item shell used for uploads, imports, and processing files
 * Provides consistent styling and structure across all list view items
 */
export const FileListItemShell: React.FC<FileListItemShellProps> = ({
  name,
  thumbnailUrl,
  youtubeUrl,
  statusText,
  progress,
  showPercentage = false,
  fileType,
  isError = false,
  onDismiss,
  onCancel,
}) => {
  const isVideo = fileType?.startsWith('video/');
  const isAudio = fileType?.startsWith('audio/');
  const ytThumbnail = getYouTubeThumbnail(youtubeUrl);
  const displayThumbnail = ytThumbnail || thumbnailUrl;

  // For audio files, don't show thumbnail - just white background with progress
  const shouldShowThumbnail = !isAudio && displayThumbnail;

  return (
    <MotionTableRow
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.7 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: {
          type: 'spring',
          stiffness: 350,
          damping: 30,
        },
        opacity: { duration: 0.2, ease: 'easeOut' },
      }}
      className="group transition-all duration-200 opacity-70 hover:opacity-80 border-b border-border/30 cursor-not-allowed bg-white dark:bg-background"
    >
      <TableCell className="py-4 px-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Thumbnail with progress indicator - horizontal aspect ratio */}
          <div className="relative h-9 w-16 rounded flex-shrink-0 overflow-hidden">
            {shouldShowThumbnail ? (
              <>
                {isVideo && thumbnailUrl ? (
                  <video
                    src={thumbnailUrl}
                    className="w-full h-full object-cover"
                    muted
                  />
                ) : (
                  <img
                    src={displayThumbnail}
                    alt={name}
                    className="w-full h-full object-cover"
                  />
                )}
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/40" />
              </>
            ) : (
              <div className="w-full h-full bg-white dark:bg-white" />
            )}
            {/* Progress indicator or error icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              {isError ? (
                <AlertCircle className="h-5 w-5 text-destructive" />
              ) : (
                <ProgressIndicator
                  progress={progress}
                  size="sm"
                  variant={shouldShowThumbnail ? 'dark' : 'light'}
                />
              )}
            </div>
          </div>

          {/* Title and status */}
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <p
              className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
              title={name}
            >
              {name}
            </p>
            <p
              className={`text-xs ${isError ? 'text-destructive' : 'text-gray-500 dark:text-gray-500'}`}
            >
              {statusText}
              {showPercentage &&
                progress !== undefined &&
                ` ${Math.round(progress)}%`}
            </p>
          </div>
        </div>
      </TableCell>
      {/* Date Column - fixed width */}
      <TableCell className="py-4 px-4 w-32" />
      {/* Action Menu Column - show dismiss/cancel button */}
      <TableCell className="py-4 w-10 pr-4">
        {isError && onDismiss && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="p-1 rounded-full hover:bg-muted transition-colors"
            title="Dismiss"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {onCancel && !isError && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            className="p-1 rounded-full hover:bg-muted transition-colors"
            title={getTranslation('upload.cancel.buttonTitle')}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </TableCell>
    </MotionTableRow>
  );
};
